/* eslint-disable react/no-array-index-key */
import {dialog} from '@/modules/layout'
import {IndividualDoseReductionSchema} from '@/modules/patients/schemas'
import {useAppForm} from '@cypro/ui/integrations/form'
import {useZero} from '@/shared/integrations/zero/use-zero'
import {toaster} from '@cypro/ui/toaster'
import {Box, Button, ButtonGroup, chakra} from '@chakra-ui/react'
import {revalidateLogic} from '@tanstack/react-form'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {IndividualDrugForm} from './individual-drug-form'
import {DrugItem} from './types'
import {isDefined} from '@cypro/ui/utils'

interface IndividualDoseReductionTabProps {
  patientId: string
  drugItems: DrugItem[]
}

function onClose() {
  dialog.close('add-dose-reduction', true)
}

export function IndividualDoseReductionTab({patientId, drugItems}: IndividualDoseReductionTabProps) {
  const {t} = useTranslate('dose-reduction')
  const zero = useZero()

  const initialValues = useMemo(() => {
    return drugItems.map(drug => {
      return {
        drugCatalogItemId: drug.id,
        name: drug.name,
      }
    })
  }, [drugItems])

  const form = useAppForm({
    defaultValues: {
      patientId,
      doseReductions: initialValues,
    } as IndividualDoseReductionSchema,
    validators: {
      onDynamic: IndividualDoseReductionSchema,
    },
    validationLogic: revalidateLogic(),
    onSubmit(props) {
      const doses: {
        drugCatalogItemId: string
        doseReduction: number
      }[] = []

      for (const dose of props.value.doseReductions) {
        if (isDefined(dose.doseReduction)) {
          doses.push({
            drugCatalogItemId: dose.drugCatalogItemId,
            doseReduction: dose.doseReduction,
          })
        }
      }

      const payload = {
        patientId: props.value.patientId,
        doseReductions: doses,
      }
      applyDoseReduction(payload)
      onClose()
    },
  })

  function onSubmit() {
    form.handleSubmit()
  }
  function applyDoseReduction(payload: Parameters<typeof zero.mutators.patients.saveDoseReduction>[0]) {
    return zero.attemptMutation(zero.mutators.patients.saveDoseReduction(payload), async mutation => {
      const data = await mutation.server
      if (!data) return

      toaster.success({title: t('success', {ns: 'common'}), description: t('applied_success')})
      form.reset()
    })
  }

  return (
    <Box display="flex" flexDirection="column" gap="4">
      <Box width="100%" overflowX="auto">
        <chakra.div display="flex" borderBottomWidth="1px" borderColor="border">
          <chakra.div
            padding="3"
            display="flex"
            alignItems="center"
            fontSize="sm"
            fontWeight="medium"
            color="fg.subtle"
            flex="1 1 0%"
          >
            {t('substance')}
          </chakra.div>
          <chakra.div
            padding="3"
            display="flex"
            alignItems="center"
            fontSize="sm"
            fontWeight="medium"
            color="fg.subtle"
            flex="1 1 0%"
          >
            {t('dose-reduction-percent')}
          </chakra.div>
        </chakra.div>
        <form.AppField name="doseReductions" mode="array">
          {field =>
            field?.state?.value?.map((ds, i) => (
              <IndividualDrugForm
                key={`doseReductions-${i}`}
                form={form}
                name={ds.name}
                fields={`doseReductions[${i}]`}
              />
            ))
          }
        </form.AppField>
      </Box>
      <ButtonGroup justifyContent="flex-end" marginTop="4">
        <Button variant="outline" colorPalette="gray" onClick={onClose}>
          {t('cancel', {ns: 'common'})}
        </Button>
        <Button size="sm" colorPalette="cyan" onClick={onSubmit}>
          {t('apply', {ns: 'common'})}
        </Button>
      </ButtonGroup>
    </Box>
  )
}
